import React from 'react';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import {
  AutoSuggest,
  AutoSuggestListItem,
} from '@lifeomic/phc-web-toolkit/src/components/AutoSuggest';
import { useIntl } from 'react-intl';

import { ChatDockRow } from './ChatDockRow';
import { ConversationEntry, FhirSubjectSearch } from './chatDockUtils';
import { chatDockMessages } from './chatDockMessages';

export type PatientSuggestion = {
  id: string;
  display: string;
  patient: fhir3.Patient;
};

type ChatDockRowClasses = {
  conversationItem: string;
  conversationItemDisabled: string;
  conversationMeta: string;
  conversationMetaActions: string;
  conversationRemove: string;
};

export const StartChatPanel = ({
  classes,
  maskedMode,
  searchValue,
  setSearchValue,
  isSearching,
  patientSuggestions,
  selectedPatient,
  handleSuggestionSelected,
  handleStartWithoutPatient,
  isStartDisabled,
}: {
  classes: {
    createChatPanel: string;
    createChatHeader: string;
  };
  maskedMode: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  isSearching: boolean;
  patientSuggestions: PatientSuggestion[];
  selectedPatient: PatientSuggestion | null;
  handleSuggestionSelected: (selected: {
    display: string;
    suggestionValue?: string;
    suggestion: PatientSuggestion;
  }) => void;
  handleStartWithoutPatient: () => void;
  isStartDisabled: boolean;
}) => {
  const { formatMessage } = useIntl();
  return (
    <Box className={classes.createChatPanel}>
      <Box className={classes.createChatHeader}>
        <Text size="caption" weight="bold">
          {formatMessage(chatDockMessages.patientContext)}
        </Text>
        <Text size="caption">
          {formatMessage(chatDockMessages.patientContextOptional)}
        </Text>
      </Box>
      <AutoSuggest
        inputPlaceholder={formatMessage(
          chatDockMessages.patientSearchPlaceholder,
        )}
        fetchSuggestions={value => setSearchValue(value)}
        getSuggestionValue={suggestion =>
          (suggestion as PatientSuggestion).display
        }
        onSuggestionSelected={suggestion =>
          handleSuggestionSelected(
            suggestion as {
              display: string;
              suggestion: PatientSuggestion;
            },
          )
        }
        renderSuggestion={(suggestion, isHighlighted) => (
          <AutoSuggestListItem
            isHighlighted={isHighlighted}
            title={(suggestion as PatientSuggestion).display}
          />
        )}
        suggestions={patientSuggestions}
        isLoading={isSearching}
        disabled={maskedMode}
        inputHelperText={
          maskedMode ? formatMessage(chatDockMessages.maskedModeDisabled) : ''
        }
        value={searchValue}
        onChange={value => setSearchValue(value)}
      />
      {selectedPatient && <Text size="caption">{selectedPatient.display}</Text>}
      <Button
        variant="outlined"
        onClick={handleStartWithoutPatient}
        disabled={isStartDisabled}
      >
        {formatMessage(chatDockMessages.startWithoutPatient)}
      </Button>
    </Box>
  );
};

export const RecentChatsPanel = ({
  classes,
  rowClasses,
  recentConversations,
  loading,
  renderConversationPreview,
  openConversation,
  handleHideConversation,
  maskedMode,
  fhirSubjectSearch,
  activeProjectId,
  activeProjectCohortId,
}: {
  classes: {
    conversationList: string;
    emptyState: string;
  };
  rowClasses: ChatDockRowClasses;
  recentConversations: ConversationEntry[];
  loading: boolean;
  renderConversationPreview: (timestamp?: string) => string | null;
  openConversation: (entry: {
    conversationId?: string;
    patientId?: string;
    title?: string;
    userIds?: string[];
  }) => void;
  handleHideConversation: (conversation: ConversationEntry) => void;
  maskedMode: boolean;
  fhirSubjectSearch: FhirSubjectSearch;
  activeProjectId: string;
  activeProjectCohortId?: string;
}) => {
  const { formatMessage } = useIntl();
  return (
    <Box className={classes.conversationList}>
      {loading && recentConversations.length === 0 ? (
        <Box className={classes.emptyState}>
          <Text size="subbody">{formatMessage(chatDockMessages.loading)}</Text>
          <DotLoader size={0} />
        </Box>
      ) : null}
      {!loading && recentConversations.length === 0 ? (
        <Box className={classes.emptyState}>
          <Text size="subbody">
            {formatMessage(chatDockMessages.noConversations)}
          </Text>
        </Box>
      ) : null}
      {recentConversations.map(conversation => (
        <ChatDockRow
          key={conversation.key}
          conversation={conversation}
          openConversation={openConversation}
          handleHideConversation={handleHideConversation}
          renderConversationPreview={renderConversationPreview}
          maskedMode={maskedMode}
          fhirSubjectSearch={fhirSubjectSearch}
          activeProjectId={activeProjectId}
          activeProjectCohortId={activeProjectCohortId}
          classes={rowClasses}
        />
      ))}
    </Box>
  );
};
